import {
  useInvalidateHome,
  useRescheduleConversation,
} from "@coaching-culture/api-client";
import { HomeNextConversationDto } from "@coaching-culture/types";
import {
  Box,
  Button,
  EmptyState,
  Flex,
  formatDate,
  formatRelative,
  Modal,
  Panel,
  Skeleton,
  Text,
  typeSize,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { fromUnixTime, getUnixTime } from "date-fns";
import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { FaRegComments } from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  CardBody,
  Eyebrow,
  HomeCard,
  LoadError,
  Muted,
  SectionTitle,
} from "./styles";
import { trackHome } from "./track";
import {
  conversationPlayUrl,
  conversationUrl,
  groupActionsByOwner,
  actionDueText,
} from "./todoPresentation";

/** Rows in the brief: a name on the left, a short detail on the right. */
const BriefList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
  }

  li + li {
    border-top: 1px solid ${(p) => p.theme.colors.grey4};
  }
`;

/** Clamps long action and goal names to two lines. */
const RowName = styled.span`
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const RowDetail = styled(Text).attrs({ as: "span" })`
  flex: none;
  max-width: 50%;
  font-size: ${typeSize("caption")};
  text-align: right;
`;

const GroupHeading = styled(Text).attrs({ as: "h4" })`
  margin: 10px 0 2px;
  font-size: ${typeSize("small")};
  font-weight: 600;
`;

const MoreLink = styled(Link)`
  font-size: ${typeSize("small")};
`;

/** Actions shown per person before "+N more". */
const ACTIONS_PER_PERSON = 3;

const AgendaList = styled.ol`
  margin: 0;
  padding-left: 20px;

  li {
    padding: 2px 0;
  }
`;

/** Agenda items listed before "and N more". */
const AGENDA_PREVIEW = 4;

const statusLabels: Record<string, string> = {
  blocked: "Blocked",
  inprogress: "In progress",
  notstarted: "Not started",
  achieved: "Achieved",
  onhold: "On hold",
};

const statusLabel = (s: string | null) =>
  s == null ? null : (statusLabels[s] ?? s);

function goalChangeText(
  c: NonNullable<HomeNextConversationDto["since"]>["goalChanges"][number],
) {
  const parts: string[] = [];
  if (c.toPercent != null && c.fromPercent !== c.toPercent) {
    parts.push(
      c.fromPercent == null
        ? `${c.toPercent}%`
        : `${c.fromPercent}% → ${c.toPercent}%`,
    );
  }
  if (c.toStatus != null && c.fromStatus !== c.toStatus) {
    parts.push(
      c.fromStatus == null
        ? `${statusLabel(c.toStatus)}`
        : `${statusLabel(c.fromStatus)} → ${statusLabel(c.toStatus)}`,
    );
  }
  return parts.join(" · ");
}

function RescheduleModal({
  next,
  onClose,
}: {
  next: HomeNextConversationDto;
  onClose: () => void;
}) {
  const [date, setDate] = useState(fromUnixTime(next.scheduledDate));
  const [error, setError] = useState(false);
  const reschedule = useRescheduleConversation(next.conversationId, next.index);
  const invalidateHome = useInvalidateHome();

  const save = async () => {
    setError(false);
    try {
      await reschedule.mutateAsync({
        scheduledDate: getUnixTime(date),
        location: next.location ?? "",
        duration: next.duration,
      });
      invalidateHome();
      onClose();
    } catch {
      setError(true);
    }
  };

  return (
    <Modal>
      <Box
        p={4}
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-reschedule-title"
      >
        <Text
          id="home-reschedule-title"
          as="h2"
          mb={3}
          fontSize="section"
          fontWeight={600}
        >
          Reschedule conversation
        </Text>
        <ReactDatePicker
          selected={date}
          onChange={(d) => d instanceof Date && setDate(d)}
          inline
          showTimeSelect
          timeIntervals={15}
        />
        {error && (
          <Text color="danger" mt={2} role="alert">
            Couldn't reschedule. Try again.
          </Text>
        )}
        <Flex justifyContent="flex-end" mt={3} style={{ gap: 8 }}>
          <Button type="button" filled={false} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            filled
            onClick={save}
            disabled={reschedule.isLoading}
          >
            {reschedule.isLoading ? "Saving…" : "Save"}
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
}

export type NextConversationCardProps = {
  next: HomeNextConversationDto | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canSchedule: boolean;
};

export function NextConversationCard({
  next,
  isLoading,
  isError,
  onRetry,
  canSchedule,
}: NextConversationCardProps) {
  const [rescheduling, setRescheduling] = useState(false);
  const [user] = useUser();

  if (isLoading) {
    return (
      <Panel as="section" aria-label="Next conversation" aria-busy="true">
        <CardBody>
          <Skeleton width="40%" height={11} mb={2} />
          <Skeleton width="75%" height={20} mb={2} />
          <Skeleton width="55%" mb={3} />
          <Skeleton height={60} />
        </CardBody>
      </Panel>
    );
  }

  if (isError) {
    return (
      <HomeCard title="Next conversation" titleId="home-next-title">
        <LoadError
          message="We couldn't load your next conversation."
          onRetry={onRetry}
        />
      </HomeCard>
    );
  }

  if (next == null) {
    return (
      <HomeCard title="Next conversation" titleId="home-next-title">
        <EmptyState
          compact
          icon={FaRegComments}
          action={
            canSchedule && (
              <Button
                small
                filled={false}
                to="/solutions/performance/conversations/create"
              >
                Schedule a conversation
              </Button>
            )
          }
        >
          You have no conversations coming up.
        </EmptyState>
      </HomeCard>
    );
  }

  const name = next.withUser.name;
  const typeName = next.type || "Conversation";
  const where = next.isOnline ? "Microsoft Teams" : next.location;
  const since = next.since;
  const actionGroups =
    since == null ? [] : groupActionsByOwner(since.openActions, user.id);
  const agendaShown = next.agenda.slice(0, AGENDA_PREVIEW);
  const hasBrief =
    since != null &&
    (since.openActions.length > 0 || since.goalChanges.length > 0);

  return (
    <Panel as="section" aria-labelledby="home-next-title">
      {rescheduling && (
        <RescheduleModal next={next} onClose={() => setRescheduling(false)} />
      )}
      <CardBody>
        <Eyebrow>Next · {formatRelative(next.scheduledDate)}</Eyebrow>
        <Text
          as="h2"
          id="home-next-title"
          color="black"
          fontSize="section"
          fontWeight={700}
          mt={1}
          mb={0}
        >
          {typeName} with {name}
          {next.role === "observer" && <Muted as="span"> (observing)</Muted>}
        </Text>
        <Muted m={0}>
          {next.duration} min
          {where && ` · ${where}`}
          {next.purpose && next.purpose !== typeName && ` · ${next.purpose}`}
        </Muted>

        {since != null && (
          <Box mt={3}>
            <SectionTitle>
              {since.lastCompletedOn != null
                ? `Since your last conversation (${formatDate(since.lastCompletedOn)})`
                : "In the last 30 days"}
            </SectionTitle>
            {hasBrief ? (
              <>
                {actionGroups.map((g) => {
                  const headingId = `home-brief-${g.key}`;
                  const more = g.actions.length - ACTIONS_PER_PERSON;
                  return (
                    <section key={g.key} aria-labelledby={headingId}>
                      <GroupHeading id={headingId}>
                        {g.label}{" "}
                        <Muted as="span" fontWeight={400}>
                          · {g.actions.length} open
                        </Muted>
                        {g.overdue > 0 && (
                          <Text as="span" color="danger" fontWeight={600}>
                            {" "}
                            · {g.overdue} overdue
                          </Text>
                        )}
                      </GroupHeading>
                      <BriefList>
                        {g.actions.slice(0, ACTIONS_PER_PERSON).map((a) => {
                          const due = actionDueText(a.dueDate);
                          return (
                            <li key={a.id}>
                              <RowName title={a.name}>{a.name}</RowName>
                              <RowDetail
                                color={due.overdue ? "danger" : "body"}
                              >
                                {due.text}
                              </RowDetail>
                            </li>
                          );
                        })}
                      </BriefList>
                      {more > 0 && (
                        <MoreLink
                          to={conversationUrl(next.conversationId, next.index)}
                        >
                          +{more} more
                        </MoreLink>
                      )}
                    </section>
                  );
                })}
                {since.goalChanges.length > 0 && (
                  <section aria-labelledby="home-brief-goals">
                    <GroupHeading id="home-brief-goals">
                      Goal progress
                    </GroupHeading>
                    <BriefList>
                      {since.goalChanges.slice(0, 3).map((c) => (
                        <li key={c.goalId}>
                          <RowName title={c.purpose}>{c.purpose}</RowName>
                          <RowDetail color="body">
                            {goalChangeText(c)}
                          </RowDetail>
                        </li>
                      ))}
                    </BriefList>
                  </section>
                )}
              </>
            ) : (
              <Muted m={0}>No open actions or goal changes.</Muted>
            )}
          </Box>
        )}

        <Box mt={3}>
          <SectionTitle>Agenda</SectionTitle>
          {next.agenda.length === 0 ? (
            <Muted m={0}>
              Nothing on the agenda yet.{" "}
              <Link to={conversationUrl(next.conversationId, next.index)}>
                Add a topic
              </Link>
            </Muted>
          ) : (
            <AgendaList>
              {agendaShown.map((x) => (
                <li key={x.id}>{x.name}</li>
              ))}
              {next.agenda.length > AGENDA_PREVIEW && (
                <Muted as="li" style={{ listStyle: "none", marginLeft: -20 }}>
                  and {next.agenda.length - AGENDA_PREVIEW} more
                </Muted>
              )}
            </AgendaList>
          )}
        </Box>

        <Flex flexWrap="wrap" mt={3} style={{ gap: 8 }}>
          <Button
            small
            color="primary"
            filled
            to={conversationUrl(next.conversationId, next.index)}
            onClick={() => trackHome("home_open_agenda", { role: next.role })}
          >
            Open agenda
          </Button>
          {next.role !== "observer" && (
            <Button
              small
              filled={false}
              to={conversationPlayUrl(next.conversationId, next.index)}
              onClick={() =>
                trackHome("home_start_conversation", { role: next.role })
              }
            >
              Start
            </Button>
          )}
          {next.canReschedule && (
            <Button
              small
              filled={false}
              type="button"
              onClick={() => {
                trackHome("home_reschedule", { role: next.role });
                setRescheduling(true);
              }}
            >
              Reschedule
            </Button>
          )}
        </Flex>
      </CardBody>
    </Panel>
  );
}
